import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  FormControl,
  Box,
  Select,
  Typography,
  Button,
  Menu,
  MenuItem,
  TablePagination,
  Avatar,
  TextField,
  Grid,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import RestoreIcon from "@mui/icons-material/Restore";
import { styled } from "@mui/system";

import Colors from "../../../../assets/styles";
import { Svgs } from "../../../../assets/images";
import { useNavigate } from "react-router-dom";
import PropertyServices from "../../../../api/PropertyServices/property.index";
import { ErrorHandler } from "../../../../utils/ErrorHandler";
import SimpleDialog from "../../../../components/Dialog/index";
import Loader from "../../../../components/Loader";
import { PropertyRoutes } from "../../../../api/PropertyServices/property.route";
import PropertyAnalyticsChart from "./PropertyAnalyticsChart";

const tableHead = [
  "SR No",
  "Unit Number",
  "Property Name",
  "Type",
  "Area",
  "Purpose",
  "Total Bookings",
  "Agents Interacted",
  "Last Booking",
  "Feedbacks",
  // "Total Selling Price",
  // "Action",
];

const CustomSelect = styled(Select)({
  "&.MuiSelect-root": {
    border: "none",
    outline: "none",
    boxShadow: "none",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "& .MuiSelect-icon": {
    color: "black",
  },
  "& .MuiSelect-select": {
    padding: "8px 32px 8px 8px",
  },
});

const PropertyAnalytics = () => {
  const [data, setData] = useState([]);
  const [id, setId] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const navigate = useNavigate();
  const [selectedPropertyId, setSelectedPropertyId] = useState("");
  const [openDialog, setOpenDialog] = useState(false);

  // filter states
  const [timeFilter, setTimeFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const today = new Date().toISOString().split("T")[0];

  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const exportOpen = Boolean(exportAnchorEl);

  const baseUrl = process.env.REACT_APP_BASE_URL;
  const sleep = () => new Promise((r) => setTimeout(r, 1000));

  const getProperties = async (
    searchParam = "",
    idParam = "",
    pageParam = 1,
    limitParam = 10,
    time = "all",
    from = "",
    to = ""
  ) => {
    setLoading(true);
    try {
      await sleep();
      const { properties, count } = await PropertyServices.getPropertyAnalytics(
        searchParam,
        idParam,
        pageParam,
        limitParam,
        time,
        from,
        to
      );
      setData(properties || []);
      setCount(count || 0);
    } catch (error) {
      ErrorHandler(error);
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getProperties(search, propertyId, page + 1, limit, timeFilter, fromDate, toDate);
  }, [page, limit]);

  const handleSearch = () => {
    getProperties(search, propertyId, 1, limit, timeFilter, fromDate, toDate);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleReset = () => {
    setSearch("");
    setTimeFilter("all");
    setFromDate("");
    setToDate("");
    setPropertyId("");
    getProperties("", "", 1, limit, "all", "", "");
  };

  const filteredData = data.filter((item) =>
    item.property.name.toLowerCase().includes(search.toLowerCase())
  );
  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "AED",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const handleExport = (type) => {
    const params = new URLSearchParams({
      search: search || "",
      timeFilter: timeFilter || "all",
      fromDate: fromDate || "",
      toDate: toDate || "",
    });

    const url = `${process.env.REACT_APP_BASE_URL}${PropertyRoutes.propertyReportExport}?${params.toString()}&format=${type}`;
    window.open(url, "_blank");
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 8,
        }}
      >
        {/* <Typography
          sx={{ fontSize: "26px", color: Colors.primary, fontWeight: "600" }}
        >
          Properties Report
        </Typography> */}
      </Box>
      <Grid item display="flex" alignItems="center" sx={{ mt: 3, pl: 2 }}
        bgcolor={Colors.backgroundColor}
        border="1px solid rgba(10,10,10,0.1)" borderRadius={2}
        gap={2} flexWrap="wrap"
      >
        <FilterAltIcon />

        {/* Property ID / Name Filter */}
        <TextField
          placeholder="Search Property"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 150 }}
        />

        {/* Time Filter */}
        <CustomSelect
          value={timeFilter}
          onChange={(e) => setTimeFilter(e.target.value)}
          displayEmpty
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="all">All Time</MenuItem>
          <MenuItem value="today">Today</MenuItem>
          <MenuItem value="weekly">This Week</MenuItem>
          <MenuItem value="monthly">This Month</MenuItem>
          <MenuItem value="yearly">This Year</MenuItem>
          <MenuItem value="custom">Custom Range</MenuItem>
        </CustomSelect>

        {/* Custom Date Range */}
        {timeFilter === "custom" && (
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              type="date"
              label="From"
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: today }}
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
            <TextField
              type="date"
              label="To"
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: today }}
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </Box>
        )}

        {/* Apply Filter */}
        <Button variant="contained" onClick={handleSearch} sx={{ bgcolor: Colors.primary }}>
          Apply Filter
        </Button>

        {/* Reset Filter */}
        <Button
          sx={{ color: Colors.red }}
          startIcon={<RestoreIcon />}
          onClick={handleReset}
        >
          Reset
        </Button>

      </Grid>

      <PropertyAnalyticsChart properties={filteredData} />

      <TableContainer
        sx={{
          mt: 2,
          backgroundColor: Colors.backgroundColor,
          borderRadius: "10px",
          display:"none"
        }}
        component={Paper}
      >
        <Table>
          <TableHead>
            <TableRow>
              {tableHead.map((column, index) => (
                <TableCell
                  key={index}
                  sx={{
                    fontWeight: "600",
                    color: Colors.black,
                    textAlign:
                      column === "Management Company" ? "left" : "center",
                  }}
                >
                  {column}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {loading ? (
            <TableRow>
              <TableCell colSpan={tableHead.length}>
                <Loader width="40px" height="40px" color={Colors.primary} />
              </TableCell>
            </TableRow>
          ) : (
            filteredData.map((row, index) => (
              <TableBody key={row._id}>
                <TableRow
                  onClick={() =>
                    navigate(`/properties/details/${row?._id}`, { state: row })
                  }
                >
                  <TableCell sx={{ textAlign: "center" }}>
                    {index + 1}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    {row?.property?.unit_number}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    {row?.property?.name}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    {row?.property?.type == "commercialOffice" ? "Commercial Office" : ""}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    {row?.property?.area + " sqft"}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", textTransform: "capitalize" }}>
                    {row?.property?.purpose == "both" ? "Sale-Rent (Both)" : row?.property?.purpose}
                  </TableCell>

                  {/* New Analytics Fields */}
                  <TableCell sx={{ textAlign: "center" }}>
                    {row?.totalBookings}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    {row?.agentNames.length || 0}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    {row?.lastBookedAt
                      ? new Date(row.lastBookedAt).toLocaleString("en-GB", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                      }).replace(",", " on")
                      : ""}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    {row?.feedbackCount || 0}
                  </TableCell>
                </TableRow>
              </TableBody>
            ))
          )}
        </Table>
        {filteredData.length > 0 && (
          <>
            <Button
              variant="contained"
              sx={{
                bgcolor: Colors.primary,
                color: "#fff",
                px: 3,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
              onClick={(e) => setExportAnchorEl(e.currentTarget)}
            >
              Export Report
            </Button>

            <Menu
              anchorEl={exportAnchorEl}
              open={exportOpen}
              onClose={() => setExportAnchorEl(null)}
            >
              <MenuItem
                onClick={() => {
                  setExportAnchorEl(null);
                  handleExport("csv");
                }}
              >
                Export as CSV
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setExportAnchorEl(null);
                  handleExport("pdf");
                }}
              >
                Export as PDF
              </MenuItem>
            </Menu>
          </>
        )}
      </TableContainer>
      <Box
        sx={{
          mt: 2,
          display: "none",
          // display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography sx={{ color: "text.secondary", ml: 2 }}>
          {`Showing ${page * limit + 1}-${Math.min(
            (page + 1) * limit,
            count
          )} of ${count}`}
        </Typography>
        <Box>
          <TablePagination
            component={Paper}
            sx={{
              borderBottom: "none",
              border: "1px solid black",
              bgcolor: "transparent",
              ".MuiTablePagination-toolbar": {
                paddingLeft: 0,
              },
              ".MuiTablePagination-spacer": {
                flex: "none",
              },
              ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
              {
                display: "none",
              },
              ".MuiTablePagination-actions": {
                marginLeft: 0,
              },
              ".MuiTablePagination-actions button:first-child": {
                borderRadius: 0,
                p: 0,
                pr: "8px",
                borderRight: "1px solid black",
              },
              ".css-78c6dr-MuiToolbar-root-MuiTablePagination-toolbar .MuiTablePagination-actions":
              {
                marginLeft: "14px !important",
              },
              ".MuiTablePagination-toolbar": {
                minHeight: "14px !important",
                padding: "0px !important",
              },
            }}
            rowsPerPageOptions={[]} // Removes rows per page selector
            count={count}
            rowsPerPage={limit}
            page={page}
            onPageChange={handlePageChange}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default PropertyAnalytics;
